import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { verifyClaimOTP } from "../../../../lib/api/claim.api";
import { Mail, AlertCircle, Clock, CheckCircle } from "lucide-react";
import { useAuth } from "../../../../Context/AuthContext";

const OTP_LENGTH = 6;
const OTP_EXPIRY_SECONDS = 10 * 60; // 10 minutes

export default function OTPVerifyPage() {
  const { claimId } = useParams<{ claimId: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth()

  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(OTP_EXPIRY_SECONDS);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer (visual only)
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const formatTime = (s: number) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const otp = digits.join("");
  const isComplete = otp.length === OTP_LENGTH && digits.every(Boolean);

  const handleDigitChange = (index: number, value: string) => {
    // Accept only digits
    const cleaned = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = cleaned;
    setDigits(next);
    setError(null);

    // Move focus forward
    if (cleaned && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((ch, i) => { next[i] = ch; });
    setDigits(next);
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleSubmit = async () => {
    if (!isComplete || !claimId || loading) return;
    setLoading(true);
    setError(null);

    try {
      await verifyClaimOTP(claimId, { otp }, accessToken);
      setSuccess(true);
      // Brief success flash then redirect
      setTimeout(() => navigate("/"), 1500);
    } catch (err: any) {
      if (err.isFallback) {
        // Backend forced proof path
        navigate(`/claim/${claimId}/proof`);
        return;
      }
      setError("Invalid or expired OTP. Please try again.");
      setDigits(Array(OTP_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center font-['DM_Sans',sans-serif]">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h2
            className="text-2xl font-semibold text-[#1C1917] mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Ownership verified!
          </h2>
          <p className="text-[#6B6460] text-sm">Redirecting you now…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F7] font-['DM_Sans',sans-serif]">
      <div className="border-b border-[#E8E4DC] bg-white px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <Mail className="w-5 h-5 text-[#C17B3F]" />
          <span className="text-sm font-medium text-[#6B6460] tracking-wide uppercase">
            Verify Identity
          </span>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-6 py-12">
        {/* Title */}
        <div className="mb-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#FDF5EC] border border-[#F0DFC5] flex items-center justify-center mx-auto mb-6">
            <Mail className="w-7 h-7 text-[#C17B3F]" />
          </div>
          <h1
            className="text-[2rem] font-semibold text-[#1C1917] leading-tight mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Check your email
          </h1>
          <p className="text-[#6B6460] text-sm leading-relaxed max-w-sm mx-auto">
            We sent a 6-digit code to the restaurant's registered email address.
            Enter it below to verify ownership.
          </p>
        </div>

        {/* Timer */}
        <div
          className={`flex items-center justify-center gap-2 mb-8 text-sm font-medium ${
            secondsLeft < 60 ? "text-red-500" : "text-[#6B6460]"
          }`}
        >
          <Clock className="w-4 h-4" />
          <span>
            {secondsLeft > 0
              ? `Code expires in ${formatTime(secondsLeft)}`
              : "Code has expired — please restart"}
          </span>
        </div>

        {/* OTP Inputs */}
        <div className="flex gap-3 justify-center mb-8" onPaste={handlePaste}>
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={digit}
              onChange={(e) => handleDigitChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              disabled={loading || secondsLeft <= 0}
              className={`w-12 h-14 text-center text-xl font-bold rounded-xl border-2 bg-white transition-all duration-150 outline-none
                ${error ? "border-red-400 text-red-600" : digit ? "border-[#C17B3F] text-[#1C1917]" : "border-[#E8E4DC] text-[#1C1917]"}
                focus:border-[#C17B3F] focus:ring-2 focus:ring-[#C17B3F]/20
                disabled:opacity-40 disabled:cursor-not-allowed`}
            />
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4 mb-6">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!isComplete || loading || secondsLeft <= 0}
          className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold text-sm tracking-wide transition-all duration-200 ${
            isComplete && !loading && secondsLeft > 0
              ? "bg-[#C17B3F] hover:bg-[#A8652F] text-white shadow-md hover:shadow-lg"
              : "bg-[#E8E4DC] text-[#B0A89E] cursor-not-allowed"
          }`}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <svg
                className="animate-spin w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  cx="12" cy="12" r="10"
                  stroke="currentColor" strokeWidth="3"
                  className="opacity-25"
                />
                <path
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  className="opacity-75"
                />
              </svg>
              Verifying…
            </span>
          ) : (
            "Verify code"
          )}
        </button>

        <p className="text-center text-xs text-[#9B918B] mt-6">
          Didn't receive a code?{" "}
          <button
            onClick={() => navigate(-1)}
            className="text-[#C17B3F] hover:underline font-medium"
          >
            Go back and try again
          </button>
        </p>
      </div>
    </div>
  );
}